import { useEffect, useMemo, useRef, useState } from 'react';
import { Banknote, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { calcularTroco, parseValorDigitado, sugestoesDePagamento } from '../troco';

interface PagamentoDinheiroModalProps {
  isOpen: boolean;
  onClose: () => void;
  total: number;
  confirmando: boolean;
  erro: string | null;
  onConfirmar: () => void;
}

const moeda = (valor: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

export function PagamentoDinheiroModal({
  isOpen,
  onClose,
  total,
  confirmando,
  erro,
  onConfirmar,
}: PagamentoDinheiroModalProps) {
  const [texto, setTexto] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTexto('');
    // Foca o campo: o caixa já está com a nota na mão.
    const foco = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(foco);
  }, [isOpen]);

  const recebido = parseValorDigitado(texto);
  const troco = calcularTroco(total, recebido);
  const suficiente = recebido !== null && troco >= 0;

  const sugestoes = useMemo(() => sugestoesDePagamento(total), [total]);

  const confirmar = () => {
    if (!suficiente || confirmando) return;
    onConfirmar();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pagamento em dinheiro">
      <div className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-500">Total da venda</p>
          <p className="text-3xl font-bold text-gray-900">{moeda(total)}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {sugestoes.map((valor) => (
            <button
              key={valor}
              type="button"
              onClick={() => setTexto(valor.toFixed(2).replace('.', ','))}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                recebido === valor
                  ? 'border-primary-600 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {valor === total ? 'Valor exato' : moeda(valor)}
            </button>
          ))}
        </div>

        <div>
          <label htmlFor="valor-recebido" className="mb-1 block text-sm font-medium text-gray-700">
            Valor recebido
          </label>
          <input
            ref={inputRef}
            id="valor-recebido"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && confirmar()}
            placeholder="0,00"
            className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-gray-300 px-3 py-3 text-right text-2xl font-semibold focus:outline-none focus:ring-1"
          />
        </div>

        {/* O troco é o número que o caixa precisa ler de longe, com a fila esperando. */}
        <div
          className={`rounded-lg p-4 text-center ${
            recebido === null ? 'bg-gray-50' : suficiente ? 'bg-green-50' : 'bg-amber-50'
          }`}
        >
          {recebido === null ? (
            <p className="text-sm text-gray-500">Informe o valor recebido para ver o troco</p>
          ) : suficiente ? (
            <>
              <p className="text-sm text-green-700">Troco</p>
              <p className="text-4xl font-bold text-green-700">{moeda(troco)}</p>
            </>
          ) : (
            <>
              <p className="text-sm text-amber-800">Ainda falta</p>
              <p className="text-3xl font-bold text-amber-800">{moeda(Math.abs(troco))}</p>
            </>
          )}
        </div>

        {erro && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={confirmando}>
            Cancelar
          </Button>
          <Button onClick={confirmar} isLoading={confirmando} disabled={!suficiente}>
            <Banknote className="mr-2 h-4 w-4" />
            Confirmar venda
          </Button>
        </div>
      </div>
    </Modal>
  );
}
